import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { LoadingIcon, CardLayout } from '@prosolve/common-components-ui'
import { getClientSubdomain } from '../../utils';
import background from '../../images/hub-app-module-background.png'
import patternBackground from '../../images/prosolve-patterns-shapes-04.png'
import QuestApexHero from '../../images/quest-apex-hero.jpg'
import SledHero from '../../images/sled-hero.jpg'
import { /* FaGlobe, */ FaShippingFast, FaQrcode, FaBookReader, FaChalkboardTeacher, FaCogs, FaBook, FaImages, FaCalendarAlt, /* FaSchool */ } from "react-icons/fa";
import useMe from '../../hooks/useMe';
import AppCard from './AppCard';

// The student background matches the student portal's GlobalStyle.js exactly (same
// stacked darkening layers over the shared pattern) so the pass-through feels seamless
const studentBackground = `
    linear-gradient(180deg, rgba(0, 19, 35, 0.82) 0%, rgba(0, 19, 35, 0.9) 100%),
    linear-gradient(rgba(0, 19, 35, 0.5), rgba(0, 19, 35, 0.5)),
    url(${patternBackground}) center center / cover no-repeat,
    #001323`

const Main = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({$isStudent, theme}) => $isStudent ? studentBackground : (theme.isDark ? `url(${theme.images.background}) top left/cover no-repeat fixed` : '#f4f6f8')};
  ${({$isStudent}) => $isStudent ? 'background-attachment: fixed;' : ''}
  // background: ${({theme}) => `url(${theme.images.background}) top left/cover no-repeat fixed`} ;
  // background: url(${background}) top left / 100% 100% no-repeat fixed; // this stretch the background but dont think looks as good
  height: calc(100vh - 40px);
  margin-top: 39px;
  overflow: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 20px 10px 20px;
  color: ${({theme}) => theme.colors.text};
  font-weight: bold;
  font-size: 40px;
  justify-content: center;
`;

const SubHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 20px;
  color: ${({theme}) => theme.colors.text};
  font-weight: bold;
  font-size: 20px;
  justify-content: center;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px;
`;

const SectionLabel = styled.div`
  display: flex;
  justify-content: center;
  color: ${({theme}) => theme.colors.text};
  font-weight: 800;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 14px;
`;

const LoadingIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 5px;
`;

export default () => {
  const clientSubdomain = getClientSubdomain()

  const isStarSubdomain = (clientSubdomain === 'star')

  const [isRedirecting, setIsRedirecting] = useState(false)

  useEffect(() => {
    setIsRedirecting(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClickedApp = (path) => {
    setIsRedirecting(true)
    // Not useNavigate via react-router-dom will not work because we need to force the SPA loaded in memory to change
    window.location.href = path;
    setIsRedirecting(false)
  }

  const apps = [
    // {
    //   id: 'eventsPortal',
    //   name: 'Events Portal',
    //   path: '/events/',
    //   group: 'internal',
    //   description: 'Manage ProSolve events, sessions, and attendee registration.',
    //   icon: (
    //     <FaGlobe
    //       style={{ fontSize: '34px', color: '#2a8d98' }}
    //     />
    //   ),
    //   color: '#2a8d98'
    // },
    {
      id: 'assessmentManager',
      name: 'Assessment Manager',
      path: '/go/assessmentManager/builder',
      group: 'internal',
      description: 'Build and manage assessments and view assessment reports.',
      icon: (
        <FaBookReader
          style={{ fontSize: '34px', color: '#0282c7' }}
        />
      ),
      color: '#0282c7'
    },
    {
      id: 'questCodeManagement',
      name: 'Quest Code Management',
      path: '/go/questCodes',
      group: 'internal',
      description: 'Create and manage Quest access codes.',
      icon: (
        <FaQrcode
          style={{ fontSize: '34px', color: '#fcd6d7' }}
        />
      ),
      color: '#fcd6d7'
    },
    {
      id: 'shippingManagement',
      name: 'Shipping Management',
      path: '/go/shipping',
      group: 'internal',
      description: 'Track, edit, and publish kit shipments.',
      icon: (
        <FaShippingFast
          style={{ fontSize: '34px', color: '#5c1be2' }}
        />
      ),
      color: '#5c1be2'
    },
    {
      id: 'assetManager',
      name: 'Asset Manager',
      path: '/go/assetManager',
      group: 'internal',
      description: 'Upload and organize the shared images and files used across the Hub.',
      icon: (
        <FaImages
          style={{ fontSize: '34px', color: '#e0a100' }}
        />
      ),
      color: '#e0a100'
    },
    {
      id: 'implementationCalendar',
      name: 'Implementation Calendar',
      path: '/go/implementationCalendar',
      group: 'internal',
      description: 'See implementation and event dates across accounts.',
      icon: (
        <FaCalendarAlt
          style={{ fontSize: '34px', color: '#2a8d98' }}
        />
      ),
      color: '#2a8d98'
    },
    {
      id: 'teacherPortal',
      name: 'Teacher Portal',
      path: '/teacher/',
      group: 'public',
      description: 'Manage classes, assign lessons, and track student growth in the ProSolve Hub.',
      icon: (
        <FaChalkboardTeacher
          style={{ fontSize: '34px', color: '#dd2d9b' }}
        />
      ),
      color: '#dd2d9b'
    },
    {
      id: 'questPortal',
      name: 'Quest Portal',
      path: '/quest/',
      group: 'public',
      description: 'Narrative-driven QUEST worlds where students take on team challenges that build critical thinking, collaboration, and communication.',
      grades: 'K-5',
      image: QuestApexHero,
      imageAlt: 'QUEST Apex Academy',
      status: 'Licensed · Active',
      color: '#ff7412'
    },
    {
      id: 'sledPortal',
      name: 'SLED Portal',
      path: '/sled/',
      group: 'public',
      description: 'Real-world, project-based expeditions that strengthen perseverance, resilience, and self-efficacy, blending hands-on facilitation with Hub data capture.',
      grades: '6-12',
      image: SledHero,
      imageAlt: 'SLED',
      imagePosition: 'center -15px',
      status: 'Licensed · Active',
      color: '#29abe3'
    },
    {
      id: 'jobsPortal',
      name: 'Jobs Portal',
      path: '/jobs/',
      group: 'internal',
      description: 'Run and monitor internal system jobs.',
      icon: (
        <FaCogs
          style={{ fontSize: '34px', color: '#895129' }}
        />
      ),
      color: '#895129'
    },
    {
      id: 'curriculumManager',
      name: 'Curriculum Manager',
      path: '/teacher/curriculumManager',
      group: 'internal',
      description: 'Manage curriculum content and pulse checks.',
      icon: (
        <FaBook
          style={{ fontSize: '34px', color: '#1db8cf' }}
        />
      ),
      color: '#1db8cf'
    },
  ]

  const {
    permissions,
    isVerified,
    eventsAccess,
    shippingAccess,
    assessmentAccess,
    questCodesAccess,
    questAccess,
    sledAccess,
    teacherAccess,
    jobsAccess,
    curriculumManagerAccess,
    assetManagerAccess,
    implementationCalendarAccess,
    isInternal
  } = useMe()

  // Students are verified logins with no permissions; they only pass through here
  // briefly before the redirect to /student/, so match the student login page styling
  const isStudent = Boolean(isVerified && !permissions)

  const allowedApps = isStarSubdomain ?
    [ apps.find(({ id }) => id === 'eventsPortal') ].filter(Boolean) :
    apps.filter(({ id }) => {
      if (id === 'eventsPortal' && !eventsAccess) {
        return false;
      } else if (id === 'assessmentManager' && !assessmentAccess) {
        return false;
       } else if (id === 'questCodeManagement' && !questCodesAccess) {
        return false;
       } else if (id === 'shippingManagement' && !shippingAccess) {
        return false;
       } else if (id === 'questPortal' && !questAccess) {
        return false;
       } else if (id === 'sledPortal' && !sledAccess) {
        return false;
       } else if (id === 'teacherPortal' && !teacherAccess) {
        return false;
       } else if (id === 'jobsPortal' && !jobsAccess) {
        return false;
       } else if (id === 'curriculumManager' && !curriculumManagerAccess) {
        return false;
       } else if (id === 'assetManager' && !assetManagerAccess) {
        return false;
       } else if (id === 'implementationCalendar' && !implementationCalendarAccess) {
        return false;
       } else {
        return true;
      }
    });

  // Auto-navigation rules:
  // - no apps (e.g. a student with no permissions) -> student app
  // - hub (HUB_USER) users who are not internal ProSolve users always land in
  //   the Hub; they reach Quest/SLED via its Programs page
  // - exactly one app -> launch it directly, no point showing a selector
  const redirectPath =
    allowedApps.length === 0 ? '/student/'
    : (!isStarSubdomain && !isInternal && teacherAccess) ? '/teacher/'
    : allowedApps.length === 1 ? allowedApps[0].path
    : null;

  useEffect(() => {
    if (redirectPath) {
      window.location.href = redirectPath;
    }
  }, [redirectPath])

  const publicApps = allowedApps.filter(({ group }) => group === 'public')
  const internalApps = allowedApps.filter(({ group }) => group === 'internal')
  const showPublicLabel = (publicApps.length > 0 && internalApps.length > 0)

  const renderAppCards = (appsToRender) => (
    <CardLayout $maxItems={3} style={{ justifyContent: 'center' }}>
      {
        appsToRender.map(({
          id,
          name,
          path,
          description,
          grades,
          image,
          imageAlt,
          imagePosition,
          status,
          icon,
          color
        }) => {
          return (
            <AppCard
              key={id}
              name={name}
              description={description}
              grades={grades}
              image={image}
              imageAlt={imageAlt}
              imagePosition={imagePosition}
              status={status}
              icon={icon}
              color={color}
              onLaunch={() => onClickedApp(path)}
            />
          )
        })
      }
    </CardLayout>
  )

  return (
    <Main $isStudent={isStudent}>
      {
        (redirectPath || isRedirecting) ?
        <LoadingIconWrapper>
          <LoadingIcon />
        </LoadingIconWrapper>
        :
        <>
          <Header>
            Welcome to the&nbsp;<span style={{ color: '#46ade0' }}>Pro</span>Solve Hub
          </Header>
          <SubHeader>
            {'Select an application below to continue'}
          </SubHeader>

          <Body>
            {
              publicApps.length > 0 &&
              <div>
                {
                  showPublicLabel &&
                  <SectionLabel>{'Public Apps'}</SectionLabel>
                }
                {renderAppCards(publicApps)}
              </div>
            }
            {
              internalApps.length > 0 &&
              <div>
                <SectionLabel>{'Internal ProSolve Apps'}</SectionLabel>
                {renderAppCards(internalApps)}
              </div>
            }
          </Body>
        </>
      }
    </Main>
  )
}
